<script setup>
import { Activity, Users, AlertTriangle, TrendingUp, Clock, Shield } from "lucide-vue-next";
import AnimatedSection from "@/components/AnimatedSection.vue";
import MetricCard from "@/components/dashboard/MetricCard.vue";
import StatusBar from "@/components/dashboard/StatusBar.vue";

const weeklyActivity = [65, 72, 58, 80, 75, 90, 85];
const days = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
</script>

<template>
  <section class="py-24 bg-secondary/30 relative overflow-hidden">
    <!-- Background texture -->
    <div class="absolute inset-0 cyber-grid opacity-50" />
    
    <div class="container mx-auto px-6 relative z-10">
      <!-- Section header -->
      <AnimatedSection animation="fade-up" class="text-center mb-16 max-w-3xl mx-auto">
        <h2 class="text-3xl md:text-4xl font-bold text-foreground mb-6">
          Todo lo que necesitas, en un solo panel
        </h2>
        <p class="text-lg text-muted-foreground">
          Un panel diseñado para directivos y responsables de equipo: claro, ordenado y con los datos que realmente importan. Actividad, productividad, alertas y rendimiento centralizados con precisión milimétrica.
        </p>
      </AnimatedSection>

      <!-- Dashboard mockup -->
      <AnimatedSection animation="scale" :delay="200" class="max-w-6xl mx-auto">
        <div class="rounded-xl border border-border/50 bg-card/80 backdrop-blur-sm shadow-2xl overflow-hidden cyan-border-glow">
          <!-- Dashboard header -->
          <div class="px-6 py-4 border-b border-border/50 flex items-center justify-between bg-background/50">
            <div class="flex items-center gap-3">
              <div class="w-3 h-3 rounded-full bg-accent/60" />
              <span class="text-sm font-medium text-foreground">Panel de Control</span>
            </div>
            <div class="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock class="w-3.5 h-3.5" />
              <span>Actualizado hace 2 min</span>
            </div>
          </div>

          <!-- Dashboard content -->
          <div class="p-6">
            <!-- Metrics row -->
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <MetricCard 
                :icon="Users"
                label="Usuarios activos"
                value="147"
                trend="+12%"
                :positive="true"
              />
              <MetricCard 
                :icon="Activity"
                label="Productividad media"
                value="87%"
                trend="+5%"
                :positive="true"
              />
              <MetricCard 
                :icon="AlertTriangle"
                label="Alertas activas"
                value="3"
                trend="-2"
                :positive="true"
              />
              <MetricCard 
                :icon="Shield"
                label="Cumplimiento"
                value="98%"
                trend="+1%"
                :positive="true"
              />
            </div>

            <!-- Charts row -->
            <div class="grid md:grid-cols-2 gap-6">
              <!-- Activity chart placeholder -->
              <div class="rounded-lg bg-background/50 border border-border/30 p-5">
                <div class="flex items-center justify-between mb-4">
                  <span class="text-sm font-medium text-foreground">Actividad semanal</span>
                  <TrendingUp class="w-4 h-4 text-accent" />
                </div>
                <div class="h-40 flex items-end gap-2">
                  <div 
                    v-for="(height, i) in weeklyActivity" 
                    :key="i" 
                    class="flex-1 flex flex-col items-center gap-2"
                  >
                    <div 
                      class="w-full rounded-t bg-gradient-to-t from-accent/40 to-accent/20 border-t border-accent/60 transition-all duration-700"
                      :style="{ height: `${height}%` }"
                    />
                    <span class="text-[10px] text-muted-foreground">
                      {{ days[i] }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Risk distribution -->
              <div class="rounded-lg bg-background/50 border border-border/30 p-5">
                <div class="flex items-center justify-between mb-4">
                  <span class="text-sm font-medium text-foreground">Estado del equipo</span>
                  <Shield class="w-4 h-4 text-accent" />
                </div>
                <div class="space-y-4">
                  <StatusBar label="Sin riesgo" :value="85" color="accent" />
                  <StatusBar label="Riesgo bajo" :value="10" color="muted" />
                  <StatusBar label="Requiere atención" :value="5" color="destructive" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>
    </div>
  </section>
</template>
